import { useEffect, useState } from "react";
import { Loader2, Music } from "lucide-react";
import type { GameType } from "@juegossc/shared";
import { GAME_SOUND_EVENTS, gameSoundSlotKey } from "@juegossc/shared";
import {
  superListSoundStyles,
  type SuperSoundStyleRow,
  type SoundAsset,
} from "../../../lib/api";
import { SoundUploader } from "./SoundUploader";

// Listado por estilo activo × juego × evento de los uploaders. Cada
// estilo es un <details> nativo que colapsa/expande. Adentro: 3 sub-
// secciones (Ruleta/Raspadita/Memoria) con los eventos del juego.
// Slot key del refactor: `${style}-${juego}-${evento}` — se calcula
// con gameSoundSlotKey() para mantener convención única.
const JUEGO_LABELS_GS: Record<GameType, string> = {
  ruleta: "Ruleta",
  raspadita: "Raspadita",
  memoria: "Memoria",
};

export function SoundsByStyleAndEvent({
  sounds,
  onChanged,
}: {
  sounds: SoundAsset[];
  onChanged: () => void;
}) {
  const [estilos, setEstilos] = useState<SuperSoundStyleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    superListSoundStyles()
      .then(setEstilos)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) {
    return (
      <div className="rounded-md bg-red-500/15 p-3 text-xs text-red-300">
        {error}
      </div>
    );
  }
  if (!estilos) {
    return (
      <div className="flex items-center gap-2 text-xs opacity-60">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Cargando estilos…
      </div>
    );
  }

  const activos = estilos.filter((e) => e.activo === 1);
  if (activos.length === 0) {
    return (
      <p className="text-xs opacity-60">
        No hay estilos activos. Activá alguno en "Estilos de sonido" o creá uno
        nuevo.
      </p>
    );
  }

  // Agrupa los eventos por juego una sola vez (es la misma lista para
  // todos los estilos). Memo no es necesario porque GAME_SOUND_EVENTS
  // es estático.
  const porJuego: Record<GameType, typeof GAME_SOUND_EVENTS[number][]> = {
    ruleta: [],
    raspadita: [],
    memoria: [],
  };
  for (const e of GAME_SOUND_EVENTS) {
    porJuego[e.juego].push(e);
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs opacity-60">
        Cada estilo activo trae su propio archivo por cada evento de cada
        juego. Si dejás un evento sin archivo, suena el sonido por defecto.
        Tamaño máx 10&nbsp;MB por archivo. MP3, WAV, OGG o WebM.
      </p>
      {activos.map((estilo) => {
        const totalEventos = GAME_SOUND_EVENTS.length;
        const cargados = GAME_SOUND_EVENTS.filter((e) =>
          sounds.some(
            (s) => s.slot_key === gameSoundSlotKey(estilo.slug, e.juego, e.evento),
          ),
        ).length;
        return (
          <details
            key={estilo.id}
            className="rounded-lg border border-white/10 bg-black/20 open:bg-black/30"
          >
            <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 marker:hidden [&::-webkit-details-marker]:hidden">
              <div className="min-w-0">
                {/* El slug técnico (ej. "navidad") se oculta de la vista
                    normal — vive en el title del nombre como tooltip. */}
                <div
                  className="flex items-center gap-2 font-medium"
                  title={estilo.slug}
                >
                  <Music className="h-4 w-4 shrink-0 text-[#a78bfa]" />
                  {estilo.label}
                </div>
                {estilo.description && (
                  <p className="mt-0.5 text-xs opacity-60">
                    {estilo.description}
                  </p>
                )}
              </div>
              <span className="shrink-0 rounded-full bg-white/10 px-2.5 py-1 text-xs">
                {cargados} / {totalEventos}
              </span>
            </summary>
            <div className="border-t border-white/10 p-4">
              <div className="flex flex-col gap-4">
                {(Object.keys(porJuego) as GameType[]).map((juego) => (
                  <div key={juego}>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wider opacity-70">
                      {JUEGO_LABELS_GS[juego]}
                    </div>
                    <div className="flex flex-col gap-2">
                      {porJuego[juego].map((spec) => {
                        const slot = gameSoundSlotKey(
                          estilo.slug,
                          spec.juego,
                          spec.evento,
                        );
                        const current = sounds.find(
                          (s) => s.slot_key === slot,
                        );
                        return (
                          <SoundUploader
                            key={slot}
                            slot={slot}
                            label={spec.label}
                            hint={spec.descripcion}
                            currentUrl={current?.url ?? null}
                            onChanged={onChanged}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </details>
        );
      })}
    </div>
  );
}
